/**
 * Configuration for HTTP request retry behavior
 */
export interface HttpRetryPolicy {
  /** Maximum number of retry attempts (0 = no retries) */
  maxRetries: number;

  /** HTTP status codes that should trigger a retry */
  retryableStatuses: number[];

  /** Strategy for calculating delay between retries */
  backoffStrategy: 'exponential' | 'linear' | 'fixed' | 'reverse-exponential' | 'reverse-linear';

  /** Initial delay in milliseconds before first retry */
  initialDelay: number;

  /** Maximum delay in milliseconds (caps exponential/linear growth, default 8000ms) */
  maxDelay?: number;

  /** Maximum number of retries at the capped delay (once max/min reached) */
  maxRetriesAtCap?: number;
}

/**
 * Default retry policy with exponential backoff
 * - 3 retry attempts
 * - Retries on network errors (0) and server errors (404, 503, 504)
 * - Exponential backoff: 1s, 2s, 4s, 8s, 16s capped at 16s
 */
export const DEFAULT_RETRY_POLICY: HttpRetryPolicy = {
  maxRetries: 3,
  retryableStatuses: [0, 404, 503, 504],
  backoffStrategy: 'exponential',
  initialDelay: 1000,
  maxDelay: 16000,
  maxRetriesAtCap: 2,
};

/**
 * No retry policy - fail immediately on error
 */
export const NO_RETRY_POLICY: HttpRetryPolicy = {
  maxRetries: 0,
  retryableStatuses: [],
  backoffStrategy: 'fixed',
  initialDelay: 0,
  maxDelay: 0,
};

/**
 * Aggressive retry policy for critical operations
 * - 5 retry attempts
 * - Exponential backoff: 500ms, 1s, 2s, 4s, 8s, 16s capped at 16s
 */
export const AGGRESSIVE_RETRY_POLICY: HttpRetryPolicy = {
  maxRetries: 5,
  retryableStatuses: [0, 408, 429, 500, 502, 503, 504],
  backoffStrategy: 'exponential',
  initialDelay: 500,
  maxDelay: 16000,
  maxRetriesAtCap: 2,
};

/**
 * Conservative retry policy for non-critical operations
 * - 2 retry attempts
 * - Linear backoff: 2s, 4s, 8s, 16s capped at 16s
 */
export const CONSERVATIVE_RETRY_POLICY: HttpRetryPolicy = {
  maxRetries: 2,
  retryableStatuses: [0, 503, 504],
  backoffStrategy: 'linear',
  initialDelay: 2000,
  maxDelay: 16000,
};

/**
 * Calculates the delay before the next retry attempt based on the policy
 * @param attempt - The current retry attempt number (0-indexed)
 * @param policy - The retry policy configuration
 * @param maxAttempts - Total maximum attempts (required for reverse strategies)
 * @returns Delay in milliseconds
 */
export function calculateRetryDelay(
  attempt: number,
  policy: HttpRetryPolicy,
  _maxAttempts?: number
): number {
  void _maxAttempts;
  const maxDelay = policy.maxDelay ?? 16000;
  let delay: number;

  switch (policy.backoffStrategy) {
    case 'exponential':
      delay = policy.initialDelay * Math.pow(2, attempt);
      break;
    case 'linear':
      delay = policy.initialDelay * (attempt + 1);
      break;
    case 'reverse-exponential': {
      const maxExponent = Math.floor(Math.log2(maxDelay / policy.initialDelay));
      const reverseExponent = Math.max(0, maxExponent - attempt);
      delay = policy.initialDelay * Math.pow(2, reverseExponent);
      break;
    }
    case 'reverse-linear': {
      const maxMultiplier = Math.floor(maxDelay / policy.initialDelay);
      const reverseMultiplier = Math.max(1, maxMultiplier - attempt);
      delay = policy.initialDelay * reverseMultiplier;
      break;
    }
    case 'fixed':
      delay = policy.initialDelay;
      break;
    default:
      delay = policy.initialDelay;
  }

  return Math.min(delay, maxDelay);
}

/**
 * Checks if retrying should continue based on capped delay limits
 * @param attempt - The current attempt number (0-indexed)
 * @param currentDelay - The calculated delay for current attempt
 * @param previousDelay - The delay from previous attempt
 * @param policy - The retry policy configuration
 * @param maxAttempts - Total maximum attempts
 * @returns true if should continue retrying
 */
export function shouldContinueRetrying(
  attempt: number,
  currentDelay: number,
  previousDelay: number,
  policy: HttpRetryPolicy,
  maxAttempts: number
): boolean {
  if (attempt >= maxAttempts) {
    return false;
  }

  const maxRetriesAtCap = policy.maxRetriesAtCap ?? Infinity;
  if (maxRetriesAtCap === Infinity) {
    return true;
  }

  const maxDelay = policy.maxDelay ?? 16000;
  const isAtMax = currentDelay >= maxDelay && previousDelay >= maxDelay;
  const isAtMin = currentDelay <= policy.initialDelay && previousDelay <= policy.initialDelay;
  const isAtCap = isAtMax || isAtMin;

  if (!isAtCap) {
    return true;
  }

  let consecutiveAtCap = 0;
  for (let i = attempt; i >= 0; i--) {
    const delay = calculateRetryDelay(i, policy, maxAttempts);
    if ((isAtMax && delay >= maxDelay) || (isAtMin && delay <= policy.initialDelay)) {
      consecutiveAtCap++;
    } else {
      break;
    }
  }

  return consecutiveAtCap <= maxRetriesAtCap;
}

/**
 * Calculates the effective maximum number of attempts based on backoff strategy
 * @param backoffStrategy - The backoff strategy to use
 * @param initialDelay - Initial delay in milliseconds
 * @param maxDelay - Maximum delay cap in milliseconds
 * @param maxRetriesAtCap - Maximum retries once cap is reached
 * @returns Effective total number of attempts
 */
export function calculateEffectiveMaxAttempts(
  backoffStrategy: 'exponential' | 'linear' | 'fixed' | 'reverse-exponential' | 'reverse-linear',
  initialDelay: number,
  maxDelay: number = 16000,
  maxRetriesAtCap?: number
): number {
  const cappedRetries = maxRetriesAtCap ?? 0;

  switch (backoffStrategy) {
    case 'exponential':
    case 'reverse-exponential': {
      const maxExponent = Math.floor(Math.log2(maxDelay / initialDelay));
      const rampSteps = maxExponent + 1;
      return rampSteps + cappedRetries;
    }
    case 'linear':
    case 'reverse-linear': {
      const maxMultiplier = Math.floor(maxDelay / initialDelay);
      const rampSteps = maxMultiplier;
      return rampSteps + cappedRetries;
    }
    case 'fixed':
      return cappedRetries > 0 ? cappedRetries : Infinity;
    default:
      return Infinity;
  }
}

/**
 * Checks if an HTTP error status should trigger a retry based on the policy
 * @param status - The HTTP error status code
 * @param policy - The retry policy configuration
 * @returns true if the error is retryable
 */
export function isRetryableError(status: number, policy: HttpRetryPolicy): boolean {
  return policy.retryableStatuses.includes(status);
}
